import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, ContentState, RichUtils, Modifier, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import * as icons from './icons';
import LinkControls from './LinkControls';
import styles from './RichTextEditor.module.css';

export interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  error?: boolean;
  id?: string;
  'data-field'?: string;
}

const createEditorState = (html: string): EditorState => {
  if (!html || html.trim() === '') {
    return EditorState.createEmpty();
  }
  const contentBlock = htmlToDraft(html);
  if (contentBlock) {
    const contentState = ContentState.createFromBlockArray(
      contentBlock.contentBlocks,
      contentBlock.entityMap
    );
    return EditorState.createWithContent(contentState);
  }
  return EditorState.createEmpty();
};

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder,
  error = false,
  id,
  ...rest
}) => {
  const [editorState, setEditorState] = useState<EditorState>(() => createEditorState(value));
  const lastEmittedHtml = useRef<string>(value);
  const [isFocused, setIsFocused] = useState(false);

  // Sync external value changes into EditorState (e.g., when API data loads)
  useEffect(() => {
    if (value !== lastEmittedHtml.current) {
      setEditorState(createEditorState(value));
      lastEmittedHtml.current = value;
    }
  }, [value]);

  const onEditorStateChange = useCallback((newState: EditorState) => {
    setEditorState(newState);
    const html = draftToHtml(convertToRaw(newState.getCurrentContent()));
    lastEmittedHtml.current = html;
    onChange(html);
  }, [onChange]);

  // Custom handleReturn: soft newline for non-list blocks (same as NoteTextEditor)
  const handleReturn = useCallback((_e: React.KeyboardEvent, _state: EditorState): boolean => {
    const content = editorState.getCurrentContent();
    const selection = editorState.getSelection();

    if (!selection.isCollapsed()) {
      return false;
    }

    const blockKey = selection.getStartKey();
    const block = content.getBlockForKey(blockKey);
    const type = block.getType();

    if (type === 'unordered-list-item' || type === 'ordered-list-item') {
      return false;
    }

    const newState = RichUtils.insertSoftNewline(editorState);
    setEditorState(newState);
    const html = draftToHtml(convertToRaw(newState.getCurrentContent()));
    lastEmittedHtml.current = html;
    onChange(html);
    return true;
  }, [editorState, onChange]);

  // Custom handlePastedText: clean consecutive newlines (same as NoteTextEditor)
  const handlePastedText = useCallback((text: string, _html: string, _state: EditorState): boolean => {
    const cleaned = text.replace(/(\r?\n){2,}/g, '\n');
    const selection = editorState.getSelection();
    const content = editorState.getCurrentContent();

    const newContent = selection.isCollapsed()
      ? Modifier.insertText(content, selection, cleaned)
      : Modifier.replaceText(content, selection, cleaned);

    const newState = EditorState.push(editorState, newContent, 'insert-characters');
    onEditorStateChange(newState);
    return true;
  }, [editorState, onEditorStateChange]);

  const containerClass = [
    styles.container,
    error ? styles.containerError : '',
    isFocused ? styles.containerFocused : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClass} id={id} {...rest}>
      <Editor
        editorState={editorState}
        onEditorStateChange={onEditorStateChange}
        handleReturn={handleReturn}
        handlePastedText={handlePastedText}
        placeholder={placeholder}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        toolbar={{
          options: ['inline', 'list', 'link', 'history', 'remove'],
          inline: {
            options: ['bold', 'italic', 'underline', 'strikethrough'],
            bold: { icon: icons.bold },
            italic: { icon: icons.italic },
            underline: { icon: icons.underline },
            strikethrough: { icon: icons.strikethrough },
          },
          list: {
            options: ['unordered', 'ordered'],
            unordered: { icon: icons.unordered },
            ordered: { icon: icons.ordered },
          },
          link: {
            component: LinkControls,
            defaultTargetOption: '_blank',
          },
          history: {
            undo: { icon: icons.undo },
            redo: { icon: icons.redo },
          },
          remove: { icon: icons.eraser },
        }}
        toolbarClassName={styles.toolbar}
        wrapperClassName={styles.wrapper}
        editorClassName={styles.editor}
      />
    </div>
  );
};

export default RichTextEditor;
